import type { SetBoundOrFieldValueArgs } from "@store/story/thunks/set-bound-or-field-value";

// Layout / visual / playback settings that belong to the element on the
// canvas, not to the media itself. On a replace they are kept as-is so the
// new video drops into the same slot; only the media-defining fields are
// swapped.
const PRESERVED_SETTINGS = [
  "width",
  "horizontalAlignment",
  "shadow",
  "round",
  "controls",
  "loop",
  "autoplay",
  "name",
] as const;

/**
 * Builds the {@link setBoundOrFieldValue} args that replace the media of an
 * existing Video element in place (Monday #12009869835) — used when the user
 * adds a video from the side panel (link / my-files / stock) while a Video
 * element is selected, mirroring the settings-panel "Change video" pop-up.
 *
 * @param entityId - The selected Video element to replace.
 * @param settings - The freshly-built video element's settings (the new media).
 */
export const buildVideoReplaceArgs = (
  entityId: string,
  settings: Record<string, unknown>,
): SetBoundOrFieldValueArgs => {
  const { src, ...rest } = settings;
  const content = Object.fromEntries(
    Object.entries(rest).filter(
      ([key]) =>
        !PRESERVED_SETTINGS.includes(key as (typeof PRESERVED_SETTINGS)[number]),
    ),
  );

  return {
    entityId,
    key: "src",
    bindingKey: "dynamicVariableSrc",
    value: src,
    extra: {
      ...content,
      // Normalize the short-form flag so replacing a YouTube short with a
      // regular video (and vice-versa) doesn't leave a stale aspect ratio.
      isShort: Boolean(settings.isShort),
      // Drop per-element edits that belonged to the previous video.
      videoCropOriginalSrc: undefined,
      videoCropData: undefined,
      videoTrimData: undefined,
      videoProcessed: undefined,
      videoPosterUrl: undefined,
      videoPosterTime: undefined,
    },
  };
};
